/**
 * Unit quaternions (x, y, z, w) for rotation composition.
 *
 * Transforms are authored as Euler degrees (see euler.ts) but composed as
 * quaternions internally, so chained rotations never suffer gimbal artifacts.
 */
// Explicit extensions: also loaded by build tooling (the asset catalog's model bounds).
import type { Vec3 } from "./vec3.ts";

/** A rotation quaternion stored as [x, y, z, w]. */
export type Quat = readonly [number, number, number, number];

export const QUAT_IDENTITY: Quat = [0, 0, 0, 1];

/** Rotation of `radians` about a unit `axis`. */
export function quatFromAxisAngle(axis: Vec3, radians: number): Quat {
  const half = radians / 2;
  const s = Math.sin(half);
  return [axis[0] * s, axis[1] * s, axis[2] * s, Math.cos(half)];
}

/**
 * Hamilton product `a * b`. Rotating by the result applies `b` first, then `a`
 * (the same convention as three's `Quaternion.multiplyQuaternions`).
 */
export function quatMultiply(a: Quat, b: Quat): Quat {
  const [ax, ay, az, aw] = a;
  const [bx, by, bz, bw] = b;
  return [
    ax * bw + aw * bx + ay * bz - az * by,
    ay * bw + aw * by + az * bx - ax * bz,
    az * bw + aw * bz + ax * by - ay * bx,
    aw * bw - ax * bx - ay * by - az * bz,
  ];
}

export function quatConjugate(q: Quat): Quat {
  return [-q[0], -q[1], -q[2], q[3]];
}

export function quatNormalize(q: Quat): Quat {
  const length = Math.hypot(q[0], q[1], q[2], q[3]);
  if (length === 0) return QUAT_IDENTITY;
  return [q[0] / length, q[1] / length, q[2] / length, q[3] / length];
}

/** Rotates `v` by the unit quaternion `q` (computes q * v * q⁻¹). */
export function quatRotateVec3(q: Quat, v: Vec3): Vec3 {
  const [qx, qy, qz, qw] = q;

  // t = 2 * cross(q.xyz, v)
  const tx = 2 * (qy * v[2] - qz * v[1]);
  const ty = 2 * (qz * v[0] - qx * v[2]);
  const tz = 2 * (qx * v[1] - qy * v[0]);

  // v' = v + w * t + cross(q.xyz, t)
  return [
    v[0] + qw * tx + (qy * tz - qz * ty),
    v[1] + qw * ty + (qz * tx - qx * tz),
    v[2] + qw * tz + (qx * ty - qy * tx),
  ];
}
